/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Gladson Samuel S
 * DifficultTopicStyles.js
 * Styles for difficult topic components
 */

import { styled } from '@mui/material/styles';
import { Badge, Box, Button, Chip, Typography } from '@mui/material';
import mixins from '../../../assets/styles/mixins.scss';
import { DashboardCardRoot, DashboardCardActionsLeftContainer } from '../../dashboardCard/styles/DashboardCardStyles';

export const ReviewCardRoot = styled(DashboardCardRoot)({
  '&&': {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: mixins.assignmentReviewBgColor,
    borderRadius: 24,
    padding: 16
  }
});

export const ActionsLeftContainer = styled(DashboardCardActionsLeftContainer)({
  '&&': {
    alignItems: 'center',
    gap: 8,
    [mixins['md-sm-min']]: {
      width: 'auto',
      flexDirection: 'row'
    }
  }
});

export const ReviewContentStyle = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'stretch',
    gap: 16,
    [mixins['mlg-max']]: {
      flexDirection: 'column'
    }
  }
});

export const LeftColumnStack = styled(Box)({
  '&&': {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    boxSizing: 'border-box'
  }
});

export const LeftPanel = styled(Box)({
  '&&': {
    flexShrink: 0
  }
});

export const RightPanel = styled(Box)({
  '&&': {
    flexShrink: 0,
    flexBasis: '420px',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    boxSizing: 'border-box',
    '& > *': {
      flex: 1,
      minHeight: 0
    },
    [mixins['mlg-max']]: {
      flexBasis: 'auto',
      width: '100%',
      '& > *': {
        flex: 'none'
      }
    }
  }
});

export const BottomReviewContentStyle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isPatternInAiUse'
})(({ isPatternInAiUse }) => ({
  '&&': {
    display: 'grid',
    gap: 16,
    width: '100%',
    boxSizing: 'border-box',
    gridTemplateColumns: isPatternInAiUse ? 'repeat(12, 1fr)' : '1fr',
    [mixins['mlg-max']]: {
      gridTemplateColumns: '1fr'
    }
  }
}));

export const SwappedLeftPanel = styled(Box)({
  '&&': {
    gridColumn: '1 / span 4',
    [mixins['mlg-max']]: {
      gridColumn: '1 / -1'
    }
  }
});

export const SwappedRightPanel = styled(Box)({
  '&&': {
    gridColumn: '5 / span 8',
    [mixins['mlg-max']]: {
      gridColumn: '1 / -1'
    }
  }
});

export const Root = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
    borderRadius: 16,
    background: mixins.chalkWhiteFont
  }
});

export const Header = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1
  }
});

export const HeaderRow = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8
  }
});

export const HeaderLeft = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flex: 1
  }
});

export const HeaderActions = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    whiteSpace: 'nowrap'
  }
});

export const FlagButton = styled(Button)({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: mixins.bulkEditCtaButtonDisabledBGColor,
    color: mixins.color_text_primary,
    textTransform: 'none',
    borderRadius: 4,
    padding: '2px 4px 4px 4px',
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    fontFamily: mixins.fontFamilyPlusSans,
    height: 32
  }
});

export const FlagIcon = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center'
  }
});

export const ReviewButton = styled(Button)({
  '&&': {
    backgroundColor: mixins.chalkWhiteFont,
    color: mixins.color_text_primary,
    textTransform: 'none',
    border: `1px solid ${mixins.reviewButtonColor}`,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    borderRadius: 4,
    lineHeight: '22px',
    fontFamily: mixins.fontFamilyPlusSans,
    padding: '8px',
    '&:hover': {
      backgroundColor: mixins.chalkWhiteFont,
      color: mixins.reviewButtonHoverColor
    }
  }
});

export const QuestionRow = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
});

export const Content = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 20
  }
});

export const ChapterHeader = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'normal',
    fontSize: mixins.fontSize12,
    lineHeight: mixins.lineHeight20,
    letterSpacing: 0,
    color: mixins.zambezi,
    verticalAlign: 'middle',
    marginBottom: 0
  },
  '&&&.MuiTypography-body1, &&&.MuiTypography-subtitle2': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontSize: mixins.fontSize12,
    lineHeight: mixins.lineHeight20,
    color: mixins.zambezi
  }
});

export const ChapterSubHeader = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'normal',
    fontSize: mixins.fontSize12,
    lineHeight: '16px',
    letterSpacing: 0,
    color: mixins.zambezi,
    verticalAlign: 'middle'
  }
});

export const Question = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontStyle: 'normal',
    fontSize: mixins.fontSize20,
    lineHeight: '28px',
    letterSpacing: '-0.4',
    color: mixins.color_text_primary,
    verticalAlign: 'middle',
    paddingLeft: 32,
    textIndent: -32,
    wordBreak: 'break-word'
  }
});

export const FlaggedChip = styled(Chip)({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    background: mixins.flaggedChipColor,
    color: mixins.color_text_primary,
    borderRadius: 6,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize12,
    lineHeight: '16px'
  }
});

export const Options = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden'
  }
});

export const OptionRow = styled(Box)({
  '&&': {
    display: 'grid',
    gridTemplateColumns: '24px 1fr auto',
    columnGap: 8,
    alignItems: 'flex-start',
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${mixins.flaggedChipColor}`,
    borderRadius: 8,
    background: mixins.chalkWhiteFont,
    boxSizing: 'border-box'
  }
});

export const OptionContent = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflow: 'hidden'
  }
});

export const OptionLabel = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'normal',
    fontSize: mixins.fontSize14,
    lineHeight: '22px',
    color: mixins.color_text_primary,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'initial',
    '& p': {
      fontFamily: mixins.fontFamilyPlusSans,
      fontWeight: mixins.fontWeight400,
      fontStyle: 'normal',
      fontSize: mixins.fontSize14,
      lineHeight: '22px',
      color: mixins.color_text_primary,
      whiteSpace: 'normal',
      marginBlock: 0
    }
  }
});

export const CorrectLabel = styled(Typography)({
  '&&': {
    color: mixins.accentGreen,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'normal',
    fontSize: mixins.fontSize14,
    lineHeight: '22px',
    whiteSpace: 'normal',
    overflow: 'visible',
    '& p': {
      color: mixins.accentGreen,
      fontFamily: mixins.fontFamilyPlusSans,
      fontWeight: mixins.fontWeight400,
      fontStyle: 'normal',
      fontSize: mixins.fontSize14,
      lineHeight: '22px',
      whiteSpace: 'normal',
      marginBlock: 0
    }
  }
});

export const WrongLabel = styled(Typography)({
  '&&': {
    color: mixins.accentRed,
    overflow: 'visible',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px',
    whiteSpace: 'normal',
    '& p': {
      color: mixins.accentRed,
      overflow: 'visible',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize14,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight400,
      lineHeight: '22px',
      whiteSpace: 'normal',
      marginBlock: 0
    }
  }
});

export const OptionSubLabel = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'normal',
    fontSize: mixins.fontSize12,
    lineHeight: '16px'
  }
});

export const CorrectSubLabel = styled(Typography)({
  '&&': {
    color: mixins.accentGreen,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    fontStyle: 'normal',
    fontSize: mixins.fontSize12,
    lineHeight: '20px'
  }
});

export const WrongSubLabel = styled(Typography)({
  '&&': {
    color: mixins.accentRed,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    fontStyle: 'normal',
    fontSize: mixins.fontSize12,
    lineHeight: '20px'
  }
});

export const Correct = styled(Box)({
  '&&': {
    border: `1px solid ${mixins.accentGreenDark}`,
    background: mixins.accentColor
  }
});

export const Wrong = styled(Box)({
  '&&': {
    border: `1px solid ${mixins.accentRedDark}`,
    background: mixins.accentLightRed
  }
});

export const OptionIcon = styled(Box)({
  '&&': {
    justifySelf: 'end',
    display: 'flex',
    alignItems: 'flex-start',
    '& .MuiSvgIcon-root': {
      width: 18,
      height: 18,
      fontSize: 18
    }
  }
});

export const StatusIcon = styled(Box)({
  '&&': {
    width: 18,
    height: 18,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

export const StatusIconCorrect = styled(Box)({
  '&&': {
    backgroundColor: mixins.accentGreen
  }
});

export const StatusIconWrong = styled(Box)({
  '&&': {
    backgroundColor: mixins.accentRed
  }
});

export const OptionPrefix = styled(Box)({
  '&&': {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    fontStyle: 'normal',
    fontSize: mixins.fontSize14,
    lineHeight: '24px',
    letterSpacing: 0,
    textAlign: 'center',
    border: `1px solid ${mixins.optionPrefixColor}`,
    color: mixins.color_text_primary,
    background: mixins.accentNotAttempted,
    padding: 0,
    opacity: 1,
    boxSizing: 'border-box'
  }
});

export const PrefixCorrect = styled(OptionPrefix)({
  '&&': {
    borderColor: mixins.accentGreenDark,
    color: mixins.accentGreen,
    background: mixins.prefixCorrectBGColor
  }
});

export const PrefixWrong = styled(OptionPrefix)({
  '&&': {
    borderColor: mixins.accentRedDark,
    color: mixins.accentRed,
    background: mixins.prefixWrongColor
  }
});

export const Footer = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    marginTop: 16,
    display: 'flex',
    alignItems: 'center'
  }
});

export const FooterIcon = styled(Box)({
  '&&': {
    width: '16px',
    height: '16px'
  }
});

export const QuestionStem = styled(Box)({
  '&&': {
    display: 'flex'
  }
});

export const QuestionNumberWrapper = styled(Box)({
  '&&': {
    display: 'flex'
  }
});

export const QuestionTitle = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    lineHeight: mixins.lineHeight175,
    letterSpacing: '-0.4px',
    '& p': {
      color: mixins.color_text_primary,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize20,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight500,
      lineHeight: mixins.lineHeight175,
      letterSpacing: '-0.4px'
    }
  },
  '&&&.MuiTypography-body1': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    lineHeight: mixins.lineHeight175,
    letterSpacing: '-0.4px'
  }
});

export const PrimaryCol1 = styled(Box)({
  '&&': {
    padding: '0px 4px'
  }
});

export const CorrectOption = styled(OptionRow)({
  '&&': {
    border: `1px solid ${mixins.accentGreenDark}`,
    background: mixins.accentColor
  }
});

export const SelectedOptions = styled(OptionRow)({
  '&&': {
    border: `1px solid ${mixins.accentRedDark}`,
    background: mixins.accentLightRed
  }
});

export const ReviewBadge = styled(Badge)({
  '&&': {
    '& .MuiBadge-badge': {
      borderRadius: 20,
      backgroundColor: mixins.accentLightRed,
      color: mixins.accentRed,
      fontFamily: mixins.fontFamilyPlusSans,
      fontWeight: mixins.fontWeight500,
      fontSize: mixins.fontSize12,
      lineHeight: 14
    }
  }
});
